import { useFormContext } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { DateInput, DateSegment, Group } from 'react-aria-components'
import { CalendarIcon, CompassIcon } from '$components'
import { DatePickerButton } from '$components'
import { CreateAppointmentSchema, Input } from '.'
import { appointmentNextAvailableDateOptions } from '$api-client/@tanstack/react-query.gen'
import { Tooltip } from '$components'
import { cn } from '$components'

export type DatePickerTriggerProps = {
  personIds: number[]
}

export function DatePickerTrigger({ personIds }: DatePickerTriggerProps) {
  const { watch, setValue } = useFormContext<CreateAppointmentSchema>()

  const duration = watch(Input.Duration)

  const { isLoading, refetch } = useQuery({
    ...appointmentNextAvailableDateOptions({
      query: {
        duration,
        groomers: personIds.length > 0 ? personIds : undefined,
        max_days_to_check: 90,
      },
    }),
    enabled: false,
  })

  const handleClick = async () => {
    if (!duration) {return}

    const date = await refetch()

    if (date.data?.date) {
      setValue(Input.Date, date.data.date)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Group className="flex border border-gray-100 rounded-xl h-12 items-center justify-between pl-3 pr-1.5 gap-2 bg-white">
        <DateInput className="flex text-sm text-gray-700">
          {(segment) => (
            <DateSegment
              segment={segment}
              className="focus:outline-none focus:shadow-[0_2px_0_0] focus:shadow-indigo-400"
            />
          )}
        </DateInput>

        <div className="flex items-center gap-1">
          <Tooltip
            disabled={isLoading}
            content={
              !duration
                ? 'Select a service to find the next available date.'
                : 'Find next available date.'
            }
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={!duration || isLoading}
              className={cn(
                'p-1.5 hover:bg-gray-50 rounded-lg text-gray-700 transition-colors focus:outline-indigo-400',
                {
                  'opacity-50 cursor-not-allowed': !duration || isLoading,
                },
              )}
            >
              <CompassIcon className="size-5 text-gray-500" />
            </button>
          </Tooltip>

          <DatePickerButton>
            <CalendarIcon className="size-5 text-gray-500" />
          </DatePickerButton>
        </div>
      </Group>
    </div>
  )
}
